/**
 * Le test d'humanité, côté visiteur : posé, éteint, prêt à s'allumer.
 *
 * Tant que `VITE_TURNSTILE_SITE_KEY` est absente, rien ne se charge : pas de
 * script tiers, pas de requête vers Cloudflare, pas de widget. La personne qui
 * remplit le formulaire ne voit aucune différence, et le serveur n'attend
 * aucun jeton.
 *
 * Le jour où on l'allume, il reste invisible dans la quasi-totalité des cas :
 * Cloudflare observe le navigateur et ne demande un geste que s'il doute. D'où
 * le rendu en mode discret plutôt qu'une case à cocher imposée à tout le monde.
 */

const URL_SCRIPT = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

type Turnstile = {
  render: (
    cible: HTMLElement,
    options: {
      sitekey: string;
      callback: (jeton: string) => void;
      "error-callback"?: () => void;
      "expired-callback"?: () => void;
      appearance?: "always" | "execute" | "interaction-only";
      language?: string;
    },
  ) => string;
  remove: (id: string) => void;
};

declare global {
  interface Window {
    turnstile?: Turnstile;
  }
}

export const cleTestHumain = (): string | null =>
  (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined) || null;

let chargement: Promise<Turnstile | null> | null = null;

/**
 * Charge le script une seule fois, et rend null s'il ne vient pas.
 *
 * Un script tiers qui ne se charge pas ne doit pas empêcher de remplir le
 * formulaire : le serveur, lui, laisse passer quand Cloudflare est injoignable,
 * et les deux côtés doivent faire le même choix.
 */
export function chargerTestHumain(): Promise<Turnstile | null> {
  if (!cleTestHumain()) return Promise.resolve(null);
  if (chargement) return chargement;

  chargement = new Promise((resolve) => {
    if (window.turnstile) return resolve(window.turnstile);
    const script = document.createElement("script");
    script.src = URL_SCRIPT;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.turnstile ?? null);
    script.onerror = () => resolve(null);
    document.head.appendChild(script);
  });
  return chargement;
}
